import { Box, Button, Flex, FormControl, FormLabel, Input, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChangeEventHandler, FormEventHandler, useEffect, useState } from 'react';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { unitAssessmentState } from '../../UnitAssessmentState';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { JSONContent } from '@tiptap/core';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';

export function ContentEditor({ refetch }: { refetch: () => void }) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'UNIT_ASSESSMENT',
      attachedToId: unitAssessmentSnap.currentAssessmentId,
    },
  });
  const [formData, setFormData] = useState({
    title: '',
  });
  const [initialContent, setInitialContent] = useState<JSONContent | undefined>(undefined);
  const [editorContent, setEditorContent] = useState<JSONContent | undefined>(undefined);

  useEffect(() => {
    const slide = unitAssessmentState.currentSlide;
    setFormData({
      title: slide?.title || '',
    });
    setInitialContent(
      (slide && 'content' in slide ? slide.content : undefined) || { type: 'doc', content: [] },
    );
  }, [unitAssessmentSnap.currentSlide, unitAssessmentSnap.isEditorOpen]);

  const createContentSlide = trpcAdmin.unitAssessment.createContentSlide.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });
  const updateContent = trpcAdmin.unitAssessment.updateContent.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const onFormChange: ChangeEventHandler<any> = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const onFormSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (unitAssessmentSnap.editorMode === EditorMode.CREATE) {
      await createContentSlide.mutateAsync({
        assessmentId: unitAssessmentSnap.currentAssessmentId,
        title: formData.title,
        content: editorContent ?? initialContent,
        unitAssessmentVersionId: unitAssessmentSnap.draftVersionId,
      });
      return;
    }
    if (unitAssessmentSnap.editorMode === EditorMode.EDIT && unitAssessmentSnap.currentSlide) {
      await updateContent.mutateAsync({
        id: unitAssessmentSnap.currentSlide.id,
        title: formData.title,
        content: editorContent ?? initialContent,
        position: unitAssessmentSnap.currentSlide.position,
      });
      return;
    }
  };

  const onChangeEditor = (content: JSONContent) => {
    setEditorContent(content);
  };

  return (
    <form onSubmit={onFormSubmit} style={{ width: '100%', minHeight: '100%' }}>
      <Flex direction="column" justifyContent="space-between" h="full" gap={4}>
        <VStack h="full" alignItems="start" gap={4}>
          <FormControl isRequired>
            <FormLabel>
              <Trans>Slide Title</Trans>
            </FormLabel>
            <Input
              autoFocus
              type="text"
              autoComplete="Off"
              placeholder="Title"
              name="title"
              value={formData.title}
              onChange={onFormChange}
            />
          </FormControl>

          <Box w="full" h="full">
            <FormLabel>
              <Trans>Slide Content</Trans>
            </FormLabel>
            <Box
              w="100%"
              borderWidth="1px"
              borderRadius="md"
              outline="1px"
              boxShadow="sm"
              minH="450px"
            >
              <DukeTiptapEditor
                initialContent={initialContent}
                onChange={onChangeEditor}
                uploadFn={uploadFile}
              />
            </Box>
          </Box>
        </VStack>
        <Flex justify={'flex-end'}>
          <Button right={0} type="submit">
            {unitAssessmentSnap.editorMode === EditorMode.CREATE ? (
              <Trans>Add</Trans>
            ) : (
              <Trans>Update</Trans>
            )}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}
